import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { ShareStrip, StatRow, StatTile } from '@/components/admin/StatTile';
import { Button, Icon, SortCaret } from '@/components/ds';
import { useSnackbar } from '@/context/SnackbarProvider';
import { useAdminUsers } from '@/hooks/admin/useAdminUsers';
import { CLIENT_GROUPS_QUERY_KEY, useClientGroups } from '@/hooks/useClientGroups';
import { AdminUser } from '@/services/api/admin';
import {
  createClientGroup,
  createMember,
  deleteMember,
} from '@/services/api/clientGroups';
import { useTranslationPrefix } from '@/translations/i18n';
import { ClientGroup } from '@/types/clientGroups';
import { formatPhone } from '@/utils/formatPhone';

// ─── Local type: derived from ClientGroup membership ──────────────────────────
// The old backend had a flat AdminRelationship record. Now we derive the same
// shape from ClientGroup → GroupMember so all existing UI code is unchanged.
// id        = GroupMember.id   (used as the "relationship ID" for delete)
// buyerId   = GroupMember.userId
// status    = derived from group.status ('active' | 'archived'; 'inactive' not used)
interface AdminRelationship {
  id: string;
  advisorId: string;
  buyerId: string;
  // Derived from ClientGroup.status. The backend filters archived groups before
  // returning, so in practice this is always 'active' — but keeping the union
  // means the type stays correct if archived groups are ever exposed.
  status: 'active' | 'archived';
  groupId: string;
}

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
  bg: 'var(--tx-fill-2)',
  card: 'var(--tx-glass-panel)',
  input: '#333333',
  border: 'var(--tx-line)',
  borderLight: 'var(--tx-line)',
  text: 'var(--tx-text)',
  muted: 'var(--tx-muted)',
  subtle: 'var(--tx-faint)',
  lime: '#c0ff02',
  danger: '#ff4d4d',
};
const font = "'Manrope', sans-serif";

// ─── Helpers ──────────────────────────────────────────────────────────────────

// ─── Helpers ─────────────────────────────────────────────────────────────────
function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function Avatar({ name, size = 36, photoUrl }: { name: string; size?: number; photoUrl?: string }) {
  const colors = ['#5AE9FF', '#FF9A4D', '#DB5282', '#C0FF02', '#FFF981', '#9A938A'];
  const idx = name.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % colors.length;
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: colors[idx],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.38,
        fontWeight: 600,
        color: 'var(--tx-text)',
        fontFamily: font,
        flexShrink: 0,
        letterSpacing: '-0.02em',
        overflow: 'hidden',
      }}>
      {photoUrl ? (
        <img
          src={photoUrl}
          alt={name}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        initials(name)
      )}
    </div>
  );
}

// ─── Card popovers ───────────────────────────────────────────────────────────
// A card chip: a tonal rounded square with a hairline glyph, never a capsule
const chipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 28,
  maxWidth: '100%',
  padding: '0 10px',
  borderRadius: 8,
  background: 'var(--tx-fill)',
  fontFamily: font,
  fontSize: 12,
  fontWeight: 500,
  color: C.text,
};

// A small glass list under its trigger, closed by a click anywhere else
function useAnchoredPopover() {
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!anchor) return;
    const close = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setAnchor(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [anchor]);
  return { anchor, setAnchor, panelRef };
}

const popoverPanel = (anchor: DOMRect, width: number, alignRight = false): React.CSSProperties => ({
  position: 'fixed',
  top: Math.min(anchor.bottom + 6, window.innerHeight - 320),
  left: alignRight ? Math.max(8, anchor.right - width) : anchor.left,
  width,
  maxHeight: 300,
  overflow: 'auto',
  scrollbarWidth: 'none',
  zIndex: 9999,
  padding: 4,
  borderRadius: 12,
  background: 'var(--tx-glass-panel)',
  backgroundImage: 'var(--tx-surface-light)',
  backdropFilter: 'blur(20px) saturate(180%)',
  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
  boxShadow: 'var(--tx-shadow)',
  fontFamily: font,
});

// Assign +: the card's matchmaking move. Lists the inbound (unassigned) clients, those in one of this advisor's markets
// first, and assigns on a click
function AssignPicker({
  advisorMarkets,
  clients,
  onAssign,
}: {
  advisorMarkets: string[];
  clients: AdminUser[];
  onAssign: (buyerId: string) => void;
}) {
  const { anchor, setAnchor, panelRef } = useAnchoredPopover();
  const fits = (c: AdminUser) => (c.profile?.markets ?? []).some((m) => advisorMarkets.includes(m));
  const ordered = [...clients].sort((a, b) => Number(fits(b)) - Number(fits(a)));
  return (
    <>
      <Button
        size="sm"
        variant="add"
        disabled={clients.length === 0}
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(anchor ? null : e.currentTarget.getBoundingClientRect());
        }}>
        Assign
      </Button>
      {anchor &&
        createPortal(
          <div ref={panelRef} onClick={(e) => e.stopPropagation()} style={popoverPanel(anchor, 280, true)}>
            <div style={{ fontSize: 11, fontWeight: 600, color: C.subtle, padding: '8px 10px 6px' }}>
              Inbound clients
            </div>
            {ordered.map((c) => {
              const where = c.profile?.markets?.[0] ?? c.profile?.city;
              return (
                <button
                  key={c.userId}
                  type="button"
                  onClick={() => {
                    onAssign(c.userId);
                    setAnchor(null);
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '6px 10px',
                    border: 0,
                    borderRadius: 8,
                    background: 'transparent',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontFamily: font,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--tx-fill)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
                  <Avatar name={c.name || c.email || '?'} size={26} photoUrl={c.profile?.photoUrl} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span
                      style={{
                        display: 'block',
                        fontSize: 13,
                        fontWeight: 500,
                        color: C.text,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                      {c.name || c.email}
                    </span>
                    <span style={{ display: 'block', fontSize: 11, fontWeight: 500, color: C.muted }}>
                      {fits(c) ? `In their market · ${where}` : where || c.email}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </>
  );
}

// The advisor's markets: the first by name, the rest a click away
// Where they work and what they speak, folded to two counts (as the original card had it); the caret opens both
// lists in a popover, so opening one never changes a card's height
function ReachMenu({ markets, languages }: { markets: string[]; languages: string[] }) {
  const { anchor, setAnchor, panelRef } = useAnchoredPopover();
  const empty = markets.length === 0 && languages.length === 0;
  const count = (icon: 'pin' | 'globe', n: number) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ display: 'flex', color: C.subtle }}>
        <Icon name={icon} size={14} />
      </span>
      <span style={{ color: n ? C.text : C.subtle, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{n}</span>
    </span>
  );
  const group = (title: string, items: string[]) =>
    items.length > 0 && (
      <div style={{ padding: '6px 6px 8px' }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: C.subtle, padding: '2px 4px 8px' }}>{title}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {items.map((m) => (
            <span key={m} style={{ ...chipStyle, height: 26, background: 'var(--tx-fill-2)' }}>
              {m}
            </span>
          ))}
        </div>
      </div>
    );
  return (
    <>
      <button
        type="button"
        disabled={empty}
        aria-expanded={!!anchor}
        aria-label={`${markets.length} markets, ${languages.length} languages`}
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(anchor ? null : e.currentTarget.getBoundingClientRect());
        }}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          height: 30,
          padding: '0 8px',
          margin: '0 -8px',
          boxSizing: 'content-box',
          border: 0,
          borderRadius: 8,
          background: anchor ? 'var(--tx-fill)' : 'transparent',
          cursor: empty ? 'default' : 'pointer',
          fontFamily: font,
          fontSize: 13,
        }}
        onMouseEnter={(e) => !empty && (e.currentTarget.style.background = 'var(--tx-fill)')}
        onMouseLeave={(e) => !anchor && (e.currentTarget.style.background = 'transparent')}>
        {count('pin', markets.length)}
        {count('globe', languages.length)}
        <span style={{ flex: 1 }} />
        {!empty && (
          <span
            style={{
              display: 'flex',
              color: C.subtle,
              transform: anchor ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.15s',
            }}>
            <Icon name="chev" size={12} />
          </span>
        )}
      </button>
      {anchor &&
        createPortal(
          <div ref={panelRef} onClick={(e) => e.stopPropagation()} style={popoverPanel(anchor, 260)}>
            {group('Markets', markets)}
            {group('Languages', languages)}
          </div>,
          document.body,
        )}
    </>
  );
}

// ─── Advisor detail drawer ───────────────────────────────────────────────────
function AdvisorDrawer({
  advisor,
  clients,
  allBuyers,
  allAssignedBuyerIds,
  onClose,
  onAssign,
  onUnassign,
  isPending,
}: {
  advisor: AdminUser;
  clients: { user: AdminUser; relationship: AdminRelationship }[];
  allBuyers: AdminUser[];
  /** Global set of buyer IDs already assigned to ANY advisor — prevents multi-assignment. */
  allAssignedBuyerIds: Set<string>;
  onClose: () => void;
  onAssign: (advisorId: string, buyerId: string) => void;
  onUnassign: (relationshipId: string) => void;
  /** True while any assign/unassign mutation is in flight — disables interactive controls. */
  isPending?: boolean;
}) {
  const [search, setSearch] = useState('');

  const availableBuyers = useMemo(() => {
    const q = search.toLowerCase();
    return allBuyers
      .filter(
        (b) =>
          !allAssignedBuyerIds.has(b.userId) &&
          (b.name?.toLowerCase().includes(q) || b.email?.toLowerCase().includes(q) || !q),
      )
      .slice(0, 20);
  }, [allBuyers, allAssignedBuyerIds, search]);

  const displayName = advisor.name || advisor.email || advisor.username;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9998,
        display: 'flex',
        justifyContent: 'flex-end',
        background: 'var(--tx-backdrop)',
        backdropFilter: 'blur(3px)',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}>
      <div
        style={{
          width: 480,
          maxWidth: '90vw',
          height: '100%',
          background: C.card,
          backgroundImage: 'var(--tx-surface-light)',
          boxShadow: 'var(--tx-shadow)',
          borderLeft: `1px solid ${C.borderLight}`,
          display: 'flex',
          flexDirection: 'column',
          animation: 'slideIn 0.2s ease-out',
        }}>
        <style>{`@keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>

        {/* Header */}
        <div style={{ padding: '24px 24px 20px', borderBottom: `1px solid ${C.borderLight}` }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 16,
            }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Avatar name={displayName} size={44} photoUrl={advisor.profile?.photoUrl} />
              <div>
                <div style={{ fontSize: 17, fontWeight: 600, color: C.text, fontFamily: font }}>
                  {displayName}
                </div>
                <div style={{ fontSize: 12, color: C.subtle, fontFamily: font }}>
                  {advisor.email || '—'}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: C.subtle,
                cursor: 'pointer',
                padding: 4,
              }}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Stats */}
          {(() => {
            const activeC = clients.filter((c) => c.relationship.status === 'active').length;
            const archivedC = clients.filter((c) => c.relationship.status === 'archived').length;
            const totalC = clients.length;
            return (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                  {[
                    { label: 'Active', value: activeC, color: activeC > 0 ? C.lime : C.subtle },
                    {
                      label: 'Closed',
                      value: archivedC,
                      color: archivedC > 0 ? '#5AE9FF' : C.subtle,
                    },
                    { label: 'Total', value: totalC, color: C.text },
                  ].map((s) => (
                    <div
                      key={s.label}
                      style={{
                        background: 'var(--tx-fill)',
                        borderRadius: 10,
                        padding: '10px 12px',
                      }}>
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          color: C.subtle,
                          fontFamily: font,
                          marginBottom: 4,
                        }}>
                        {s.label}
                      </div>
                      <div
                        style={{ fontSize: 17, fontWeight: 600, color: s.color, fontFamily: font }}>
                        {s.value}
                      </div>
                    </div>
                  ))}
                </div>
                {totalC > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <div
                      style={{
                        display: 'flex',
                        height: 6,
                        borderRadius: 3,
                        overflow: 'hidden',
                        background: 'var(--tx-fill)',
                      }}>
                      {activeC > 0 && (
                        <div
                          style={{
                            width: `${(activeC / totalC) * 100}%`,
                            background: C.lime,
                            transition: 'width 0.3s',
                          }}
                        />
                      )}
                      {archivedC > 0 && (
                        <div
                          style={{
                            width: `${(archivedC / totalC) * 100}%`,
                            background: '#5AE9FF',
                            transition: 'width 0.3s',
                          }}
                        />
                      )}
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>

        {/* Current clients */}
        <div style={{ flex: 1, overflow: 'auto', padding: '16px 24px' }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 600,
              color: C.subtle,
              fontFamily: font,
              marginBottom: 12,
            }}>
            Current Clients ({clients.length})
          </div>
          {clients.length === 0 ? (
            <div
              style={{
                fontSize: 12,
                color: C.subtle,
                fontFamily: font,
                padding: '20px 0',
                textAlign: 'center',
              }}>
              No clients assigned yet
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {clients.map(({ user: buyer, relationship: rel }) => (
                <div
                  key={buyer.userId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 12px',
                    borderRadius: 10,
                    background: 'var(--tx-fill)',
                  }}>
                  <Avatar
                    name={buyer.name || buyer.email || '?'}
                    size={28}
                    photoUrl={buyer.profile?.photoUrl}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: C.text,
                        fontFamily: font,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                      {buyer.name || buyer.email || buyer.userId}
                    </div>
                    {buyer.name && buyer.email && (
                      <div
                        style={{
                          fontSize: 11,
                          color: C.subtle,
                          fontFamily: font,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                        {buyer.email}
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: rel.status === 'active' ? C.lime : C.subtle,
                      fontFamily: font,
                    }}>
                    {rel.status}
                  </div>
                  <button
                    onClick={() => {
                      if (!isPending) onUnassign(rel.id);
                    }}
                    title={isPending ? 'Please wait…' : 'Remove'}
                    disabled={isPending}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: C.subtle,
                      cursor: isPending ? 'not-allowed' : 'pointer',
                      padding: 2,
                      display: 'flex',
                      alignItems: 'center',
                      opacity: isPending ? 0.35 : 1,
                    }}
                    onMouseEnter={(e) => {
                      if (!isPending) (e.currentTarget as HTMLElement).style.color = C.danger;
                    }}
                    onMouseLeave={(e) => {
                      if (!isPending) (e.currentTarget as HTMLElement).style.color = C.subtle;
                    }}>
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add client search */}
          <div style={{ marginTop: 20 }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: C.subtle,
                fontFamily: font,
                marginBottom: 8,
              }}>
              Add Client
            </div>
            <div style={{ position: 'relative' }}>
              <svg
                style={{
                  position: 'absolute',
                  left: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                }}
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke={C.subtle}
                strokeWidth="2"
                strokeLinecap="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search clients by name or email…"
                style={{
                  width: '100%',
                  height: 34,
                  background: 'var(--tx-fill)',
                  border: 0,
                  borderRadius: 8,
                  padding: '0 12px 0 32px',
                  color: C.text,
                  fontSize: 12,
                  fontFamily: font,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = C.muted;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = C.border;
                }}
              />
            </div>
            {(search.trim() || availableBuyers.length > 0) && (
              <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {availableBuyers.length === 0 ? (
                  <div
                    style={{
                      fontSize: 11,
                      color: C.subtle,
                      fontFamily: font,
                      padding: '8px 0',
                      textAlign: 'center',
                    }}>
                    No matching clients
                  </div>
                ) : (
                  availableBuyers.map((buyer) => (
                    <div
                      key={buyer.userId}
                      onClick={() => {
                        if (isPending) return;
                        onAssign(advisor.userId, buyer.userId);
                        setSearch('');
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 10px',
                        borderRadius: 8,
                        cursor: isPending ? 'not-allowed' : 'pointer',
                        transition: 'background 0.12s',
                        opacity: isPending ? 0.5 : 1,
                      }}
                      onMouseEnter={(e) => {
                        if (!isPending)
                          (e.currentTarget as HTMLElement).style.background =
                            'rgba(255,255,255,0.06)';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLElement).style.background = 'transparent';
                      }}>
                      <Avatar
                        name={buyer.name || buyer.email || '?'}
                        size={24}
                        photoUrl={buyer.profile?.photoUrl}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: C.text,
                            fontFamily: font,
                          }}>
                          {buyer.name || buyer.email}
                        </span>
                        {buyer.name && buyer.email && (
                          <span
                            style={{
                              fontSize: 11,
                              color: C.subtle,
                              fontFamily: font,
                              marginLeft: 6,
                            }}>
                            {buyer.email}
                          </span>
                        )}
                      </div>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={C.lime}
                        strokeWidth="2"
                        strokeLinecap="round">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── Main tab ────────────────────────────────────────────────────────────────
export const RelationshipsTab = () => {
  const { showAlert } = useSnackbar();
  const t = useTranslationPrefix('toasts');
  // ── Data ─────────────────────────────────────────────────────────────────────
  const { users, isLoading: usersLoading } = useAdminUsers();
  const queryClient = useQueryClient();

  // ── State ─────────────────────────────────────────────────────────────────────
  const [advisorSearch, setAdvisorSearch] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [advisorView, setAdvisorView] = useState<'list' | 'card'>('card');
  const [marketFilter, setMarketFilter] = useState<string | null>(null);
  const [languageFilter, setLanguageFilter] = useState<string | null>(null);
  const [marketDropdownOpen, setMarketDropdownOpen] = useState(false);
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);

  // Close filter dropdowns on outside click
  useEffect(() => {
    if (!marketDropdownOpen && !languageDropdownOpen) return;
    const handler = () => {
      setMarketDropdownOpen(false);
      setLanguageDropdownOpen(false);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [marketDropdownOpen, languageDropdownOpen]);

  // Cleanup drop-rejection timer on unmount
  useEffect(
    () => () => {
      if (dropRejectedTimerRef.current) clearTimeout(dropRejectedTimerRef.current);
    },
    [],
  );

  const [selectedAdvisorId, setSelectedAdvisorId] = useState<string | null>(null);
  const [dragBuyerId, setDragBuyerId] = useState<string | null>(null);
  const [dragOverAdvisorId, setDragOverAdvisorId] = useState<string | null>(null);
  const [confirmUnassign, setConfirmUnassign] = useState<{
    relationshipId: string;
    buyerName: string;
    advisorName: string;
  } | null>(null);
  const [sortCol, setSortCol] = useState<
    'name' | 'role' | 'assigned' | 'clients' | 'deals' | 'offers'
  >('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  // Brief notification shown when a drag-drop is rejected (buyer already assigned)
  const [dropRejected, setDropRejected] = useState(false);
  const dropRejectedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Derived users ──────────────────────────────────────────────────────────────
  const userMap = useMemo(() => new Map(users.map((u) => [u.userId, u])), [users]);
  const advisors = useMemo(() => users.filter((u) => u.groups?.includes('Advisor')), [users]);
  const buyers = useMemo(() => users.filter((u) => u.groups?.includes('Buyer')), [users]);

  // ── Every advisor's client groups ─────────────────────────────────────────────
  // Through the app's shared hook: it owns the CLIENT_GROUPS_QUERY_KEY cache (paged, as useInfiniteQuery) and pages
  // through the admin "all advisors" list in the background. A plain useQuery on the same key read the other shape
  // (and only the first page), so every advisor showed no clients, deals or offers
  const { groups: allGroups, isLoading: groupsLoading, error: groupsError } = useClientGroups();

  const advisorGroupsMap = useMemo(() => {
    const map = new Map<string, ClientGroup[]>();
    advisors.forEach((a) => map.set(a.userId, [])); // seed so every advisor has a defined entry
    allGroups.forEach((group) => {
      const list = map.get(group.advisorId);
      if (list) list.push(group);
      // Groups belonging to advisors not in the current users list are silently ignored
    });
    return map;
  }, [advisors, allGroups]);

  // ── Derive relationships from group membership ──────────────────────────────────
  // Each GroupMember with a userId becomes one AdminRelationship.
  const relationships = useMemo((): AdminRelationship[] => {
    const rels: AdminRelationship[] = [];
    advisors.forEach((advisor) => {
      const groups = advisorGroupsMap.get(advisor.userId) ?? [];
      groups.forEach((group) => {
        group.members.forEach((member) => {
          if (member.userId) {
            rels.push({
              id: member.id,
              advisorId: advisor.userId,
              buyerId: member.userId,
              status: group.status === 'active' ? 'active' : 'archived',
              groupId: group.id,
            });
          }
        });
      });
    });
    return rels;
  }, [advisors, advisorGroupsMap]);

  const isLoading = usersLoading || groupsLoading;
  const error = groupsError as Error | null;

  // ── Mutations ──────────────────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: async ({ advisorId, buyerId }: { advisorId: string; buyerId: string }) => {
      const buyer = userMap.get(buyerId);
      const memberData = {
        name: buyer?.name || buyer?.email || '',
        email: buyer?.email || '',
        phone: '',
        role: 'Buyer',
        userId: buyerId,
      };
      // Always create a new group for each assigned buyer — the admin is establishing a
      // new relationship, not adding a co-buyer to an existing client. Re-using an active
      // group would merge unrelated clients into the same holding, which is the bug.
      // advisorId is passed so the backend creates the group under the advisor's account,
      // not the admin's. The backend ignores this field for non-admin callers.
      const { group } = await createClientGroup({
        holdingName: `${buyer?.name || buyer?.email || 'New'} Group`,
        advisorId,
      });
      await createMember(group.id, memberData);
    },
    // onSettled ensures invalidation fires on both success AND error, keeping the
    // UI in sync even when one of the sequential calls fails mid-flight.
    onSettled: () => queryClient.invalidateQueries({ queryKey: CLIENT_GROUPS_QUERY_KEY }),
  });

  const deleteMutation = useMutation({
    mutationFn: (memberId: string) => deleteMember(memberId),
    onSettled: () => queryClient.invalidateQueries({ queryKey: CLIENT_GROUPS_QUERY_KEY }),
  });

  // ── Derived data ───────────────────────────────────────────────────────────────

  // Collect all unique markets and languages present across advisors
  const { allMarkets, allLanguages } = useMemo(() => {
    const markets = new Set<string>();
    const languages = new Set<string>();
    advisors.forEach((a) => {
      (a.profile?.markets ?? []).forEach((m) => markets.add(m));
      (a.profile?.languages ?? []).forEach((l) => languages.add(l));
    });
    return { allMarkets: [...markets].sort(), allLanguages: [...languages].sort() };
  }, [advisors]);

  // Build advisor → clients map
  const advisorClientsMap = useMemo(() => {
    const map = new Map<string, { user: AdminUser; relationship: AdminRelationship }[]>();
    advisors.forEach((a) => map.set(a.userId, []));
    relationships.forEach((rel) => {
      const buyer = userMap.get(rel.buyerId);
      if (buyer) {
        const list = map.get(rel.advisorId) || [];
        list.push({ user: buyer, relationship: rel });
        map.set(rel.advisorId, list);
      }
    });
    return map;
  }, [advisors, relationships, userMap]);

  // Deal and offer counts per advisor (aggregated from already-fetched group data)
  const advisorStatsMap = useMemo(() => {
    const map = new Map<string, { dealCount: number; offerCount: number }>();
    advisors.forEach((advisor) => {
      const groups = advisorGroupsMap.get(advisor.userId) ?? [];
      let dealCount = 0;
      let offerCount = 0;
      groups.forEach((group) => {
        dealCount += group.deals?.length ?? 0;
        group.deals?.forEach((deal) => {
          const listings = deal.listings;
          if (listings && listings.length > 0) {
            offerCount += listings.reduce((sum, l) => sum + (l.offersUsed ?? 0), 0);
          } else {
            offerCount += deal.offersUsed ?? 0;
          }
        });
      });
      map.set(advisor.userId, { dealCount, offerCount });
    });
    return map;
  }, [advisors, advisorGroupsMap]);

  // Platform-wide deal and offer totals (for top stat cards)
  const { totalDeals, totalOffers } = useMemo(() => {
    let deals = 0;
    let offers = 0;
    advisorStatsMap.forEach(({ dealCount, offerCount }) => {
      deals += dealCount;
      offers += offerCount;
    });
    return { totalDeals: deals, totalOffers: offers };
  }, [advisorStatsMap]);

  // Assigned buyer IDs (fast membership test used throughout)
  const assignedBuyerIds = useMemo(
    () => new Set(relationships.map((r) => r.buyerId)),
    [relationships],
  );

  // O(1) lookup from buyerId → relationship (avoids O(n×m) .find inside render loops)
  const buyerRelationshipMap = useMemo(
    () => new Map(relationships.map((r) => [r.buyerId, r])),
    [relationships],
  );

  // Filtered advisors — search by name, email, markets, languages, phone, DRE
  const filteredAdvisors = useMemo(() => {
    const q = advisorSearch.toLowerCase();
    return advisors.filter((a) => {
      const markets = a.profile?.markets ?? [];
      const languages = a.profile?.languages ?? [];
      if (marketFilter && !markets.includes(marketFilter)) return false;
      if (languageFilter && !languages.includes(languageFilter)) return false;
      if (!q) return true;
      if (a.name?.toLowerCase().includes(q) || a.email?.toLowerCase().includes(q)) return true;
      const phone = formatPhone(a.profile?.phoneNumber);
      const dre = a.profile?.dreNumber ?? '';
      return (
        markets.some((m) => m.toLowerCase().includes(q)) ||
        languages.some((l) => l.toLowerCase().includes(q)) ||
        phone.includes(q) ||
        dre.includes(q)
      );
    });
  }, [advisors, advisorSearch, marketFilter, languageFilter]);

  // Filtered clients — unassigned first
  const filteredClients = useMemo(() => {
    const q = clientSearch.toLowerCase();
    const filtered = buyers.filter(
      (b) => !q || b.name?.toLowerCase().includes(q) || b.email?.toLowerCase().includes(q),
    );
    return filtered.sort((a, b) => {
      const aAssigned = assignedBuyerIds.has(a.userId) ? 1 : 0;
      const bAssigned = assignedBuyerIds.has(b.userId) ? 1 : 0;
      return aAssigned - bAssigned;
    });
  }, [buyers, clientSearch, assignedBuyerIds]);

  const unassignedBuyers = useMemo(
    () => buyers.filter((b) => !assignedBuyerIds.has(b.userId)),
    [buyers, assignedBuyerIds],
  );
  const unassignedCount = unassignedBuyers.length;
  const advisorsWithClients = advisors.filter((a) => (advisorClientsMap.get(a.userId)?.length ?? 0) > 0).length;
  const advisorsWithDeals = advisors.filter((a) => (advisorStatsMap.get(a.userId)?.dealCount ?? 0) > 0).length;
  const advisorsWithOffers = advisors.filter((a) => (advisorStatsMap.get(a.userId)?.offerCount ?? 0) > 0).length;

  // Grid template for advisor list view
  const ADVISOR_GRID = '44px minmax(0, 2fr) minmax(100px, auto) 100px 80px 80px 80px 20px';

  // Sort advisors
  const sortedAdvisors = useMemo(() => {
    const list = [...filteredAdvisors];
    list.sort((a, b) => {
      let av: string | number = 0;
      let bv: string | number = 0;
      const aC = advisorClientsMap.get(a.userId) || [];
      const bC = advisorClientsMap.get(b.userId) || [];
      switch (sortCol) {
        case 'name':
          av = (a.name || a.email || '').toLowerCase();
          bv = (b.name || b.email || '').toLowerCase();
          break;
        case 'role':
          av = (a.groups?.[0] || '').toLowerCase();
          bv = (b.groups?.[0] || '').toLowerCase();
          break;
        case 'assigned':
          av = aC.length;
          bv = bC.length;
          break;
        case 'clients':
          av = aC.length;
          bv = bC.length;
          break;
        case 'deals':
          av = advisorStatsMap.get(a.userId)?.dealCount ?? 0;
          bv = advisorStatsMap.get(b.userId)?.dealCount ?? 0;
          break;
        case 'offers':
          av = advisorStatsMap.get(a.userId)?.offerCount ?? 0;
          bv = advisorStatsMap.get(b.userId)?.offerCount ?? 0;
          break;
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [filteredAdvisors, sortCol, sortDir, advisorClientsMap, advisorStatsMap]);

  const toggleSort = useCallback(
    (col: typeof sortCol) => {
      if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      else {
        setSortCol(col);
        setSortDir('asc');
      }
    },
    [sortCol],
  );

  // Selected advisor for drawer
  const selectedAdvisor = selectedAdvisorId ? userMap.get(selectedAdvisorId) : null;
  const selectedAdvisorClients = selectedAdvisorId
    ? advisorClientsMap.get(selectedAdvisorId) || []
    : [];

  // ── Drag handlers ──────────────────────────────────────────────────────────────
  const handleDragStart = useCallback((buyerId: string) => {
    setDragBuyerId(buyerId);
  }, []);
  const handleDragEnd = useCallback(() => {
    setDragBuyerId(null);
    setDragOverAdvisorId(null);
  }, []);

  const handleDrop = useCallback(
    (advisorId: string) => {
      // Block concurrent drops while a mutation is in flight. Without this guard,
      // multiple rapid drops to advisors with no active group each see "no group"
      // and each create a new one, producing duplicate active groups.
      if (createMutation.isPending) {
        setDragBuyerId(null);
        setDragOverAdvisorId(null);
        return;
      }
      if (dragBuyerId) {
        if (!assignedBuyerIds.has(dragBuyerId)) {
          createMutation.mutate(
            { advisorId, buyerId: dragBuyerId },
            {
              onSuccess: () => showAlert(t('relationship.created'), 'success'),
              onError: () => showAlert(t('relationship.create_failed'), 'error'),
            },
          );
        } else {
          // Buyer was assigned between drag-start and drop (race condition guard).
          // Show a brief notification so the admin understands why the drop was ignored.
          if (dropRejectedTimerRef.current) clearTimeout(dropRejectedTimerRef.current);
          setDropRejected(true);
          dropRejectedTimerRef.current = setTimeout(() => setDropRejected(false), 2500);
        }
      }
      setDragBuyerId(null);
      setDragOverAdvisorId(null);
    },
    [dragBuyerId, assignedBuyerIds, createMutation],
  );

  const handleAssign = useCallback(
    (advisorId: string, buyerId: string) => {
      createMutation.mutate(
        { advisorId, buyerId },
        {
          onSuccess: () => showAlert(t('relationship.created'), 'success'),
          onError: () => showAlert(t('relationship.create_failed'), 'error'),
        },
      );
    },
    [createMutation, showAlert, t],
  );

  const handleUnassign = useCallback(
    (relationshipId: string) => {
      deleteMutation.mutate(relationshipId, {
        onSuccess: () => showAlert(t('relationship.removed'), 'success'),
        onError: () => showAlert(t('relationship.remove_failed'), 'error'),
      });
      setConfirmUnassign(null);
    },
    [deleteMutation, showAlert, t],
  );

  // ── Render ─────────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div style={{ padding: 40 }}>
        <div style={{ background: C.card, borderRadius: 16, padding: 24, fontFamily: font }}>
          <span style={{ color: C.danger, fontSize: 14 }}>
            Error loading relationships: {error.message}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        height: '100vh',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        padding: '20px 0',
        scrollbarWidth: 'none',
        fontFamily: font,
        background: 'transparent',
      }}>
      {/* ─── Stat cards: the app's tile, value first, label beneath, a share strip and a caption ─── */}
      <StatRow columns={4}>
        <StatTile
          loading={isLoading}
          value={advisors.length}
          label="Advisors"
          visual={
            <ShareStrip
              total={advisors.length}
              parts={[{ label: 'With clients', color: '#FF9A4D', value: advisorsWithClients }]}
            />
          }
          caption={`${advisorsWithClients} with clients`}
        />
        <StatTile
          loading={isLoading}
          value={buyers.length}
          label="Clients"
          visual={
            <ShareStrip
              parts={[
                { label: 'Matched', color: '#5AE9FF', value: buyers.length - unassignedCount },
                { label: 'Inbound', color: '#DB5282', value: unassignedCount },
              ]}
            />
          }
          caption={[`${buyers.length - unassignedCount} matched`, `${unassignedCount} inbound`]}
        />
        <StatTile
          loading={isLoading}
          value={totalDeals}
          label="Active deals"
          visual={
            <ShareStrip total={advisors.length} parts={[{ label: 'Advisors working', color: '#FFF981', value: advisorsWithDeals }]} />
          }
          caption={`${advisorsWithDeals} advisors working`}
        />
        <StatTile
          loading={isLoading}
          value={totalOffers}
          label="Offers"
          visual={
            <ShareStrip
              total={advisors.length}
              parts={[{ label: 'Advisors with offers', color: '#C0FF02', value: advisorsWithOffers }]}
            />
          }
          caption={totalDeals ? `${(totalOffers / totalDeals).toFixed(1)} per deal` : 'No deals yet'}
        />
      </StatRow>

      {/* Drop-rejection notice — shown briefly when a drag-drop is blocked */}
      {dropRejected && (
        <div
          style={{
            marginBottom: 10,
            padding: '9px 16px',
            borderRadius: 10,
            background: '#DB528218',
            border: 0,
            fontSize: 12,
            fontWeight: 500,
            color: '#DB5282',
            fontFamily: font,
          }}>
          This client is already assigned to an advisor. Unassign them first to reassign.
        </div>
      )}

      {/* ─── Two-panel layout ───────────────────────────────── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 380px',
          gap: 16,
          flex: 1,
          minHeight: 0,
        }}>
        {/* ─── Left panel: Advisors ─────────────────────────── */}
        <div
          style={{
            background: C.card,
            backgroundImage: 'var(--tx-surface-light)',
            boxShadow: 'var(--tx-shadow)',
            borderRadius: '24px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}>
          {/* Header */}
          <div
            style={{
              padding: '16px 20px',
              borderBottom: `1px solid ${C.borderLight}`,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 300,
                color: C.muted,
                fontFamily: font,
              }}>
              Advisors
            </div>
            <div style={{ flex: 1 }} />
            <div style={{ position: 'relative' }}>
              <svg
                style={{
                  position: 'absolute',
                  left: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                }}
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke={C.subtle}
                strokeWidth="2"
                strokeLinecap="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                value={advisorSearch}
                onChange={(e) => setAdvisorSearch(e.target.value)}
                placeholder="Search advisors…"
                style={{
                  width: 200,
                  height: 32,
                  background: 'var(--tx-fill)',
                  border: 0,
                  borderRadius: 8,
                  padding: '0 12px 0 32px',
                  color: C.text,
                  fontSize: 12,
                  fontFamily: font,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = C.muted;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = C.border;
                }}
              />
            </div>
            {/* View toggle */}
            <div
              style={{
                display: 'flex',
                background: 'var(--tx-fill)',
                borderRadius: 8,
                overflow: 'hidden',
                height: 32,
              }}>
              {/* List icon */}
              <button
                onClick={() => setAdvisorView('list')}
                style={{
                  background: advisorView === 'list' ? 'var(--tx-fill-2)' : 'transparent',
                  border: 'none',
                  padding: '0 10px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  color: advisorView === 'list' ? C.text : C.subtle,
                  height: '100%',
                }}
                title="List view">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round">
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
              </button>
              {/* Card icon */}
              <button
                onClick={() => setAdvisorView('card')}
                style={{
                  background: advisorView === 'card' ? 'var(--tx-fill-2)' : 'transparent',
                  border: 'none',
                  padding: '0 10px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  color: advisorView === 'card' ? C.text : C.subtle,
                  height: '100%',
                }}
                title="Card view">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7" rx="1" />
                  <rect x="14" y="3" width="7" height="7" rx="1" />
                  <rect x="3" y="14" width="7" height="7" rx="1" />
                  <rect x="14" y="14" width="7" height="7" rx="1" />
                </svg>
              </button>
            </div>
          </div>

          {/* Advisor content */}
          <div
            style={{
              flex: 1,
              overflow: 'auto',
              padding: advisorView === 'card' ? '12px 16px' : 0,
            }}>
            {isLoading ? (
              <div
                style={{
                  padding: 40,
                  textAlign: 'center',
                  fontSize: 13,
                  color: C.subtle,
                  fontFamily: font,
                }}>
                Loading…
              </div>
            ) : filteredAdvisors.length === 0 ? (
              <div
                style={{
                  padding: 40,
                  textAlign: 'center',
                  fontSize: 13,
                  color: C.subtle,
                  fontFamily: font,
                }}>
                No advisors found
              </div>
            ) : advisorView === 'list' ? (
              /* ─── List view ─────────────────────────────────────── */
              <div>
                {/* Filter by row */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '10px 20px',
                    fontSize: 11,
                    fontFamily: font,
                    color: C.subtle,
                    borderBottom: `1px solid ${C.borderLight}`,
                    justifyContent: 'flex-end',
                  }}>
                  <span style={{ fontWeight: 500 }}>Filter by</span>
                  {/* Market filter */}
                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setMarketDropdownOpen((o) => !o);
                        setLanguageDropdownOpen(false);
                      }}
                      style={{
                        background: marketFilter ? 'var(--tx-fill-2)' : 'transparent',
                        border: 0,
                        borderRadius: 6,
                        padding: '4px 10px',
                        fontSize: 11,
                        fontWeight: 500,
                        color: marketFilter ? C.text : C.muted,
                        fontFamily: font,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                      }}>
                      {marketFilter || 'Market'}
                      <svg
                        width="8"
                        height="8"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round">
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </button>
                    {marketDropdownOpen && (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          position: 'absolute',
                          top: 28,
                          left: 0,
                          zIndex: 100,
                          background: 'var(--tx-glass)',
                          backdropFilter: 'var(--tx-blur)',
                          borderRadius: '12px',
                          padding: '6px 4px',
                          minWidth: 160,
                          boxShadow: 'var(--tx-shadow-sm)',
                        }}>
                        <div
                          onMouseDown={() => {
                            setMarketFilter(null);
                            setMarketDropdownOpen(false);
                          }}
                          style={{
                            padding: '6px 12px',
                            fontSize: 12,
                            fontFamily: font,
                            cursor: 'pointer',
                            borderRadius: 6,
                            color: !marketFilter ? C.text : C.muted,
                            background: !marketFilter ? 'var(--tx-fill-2)' : 'transparent',
                            fontWeight: !marketFilter ? 600 : 400,
                          }}>
                          All Markets
                        </div>
                        {allMarkets.map((m) => (
                          <div
                            key={m}
                            onMouseDown={() => {
                              setMarketFilter(m);
                              setMarketDropdownOpen(false);
                            }}
                            style={{
                              padding: '6px 12px',
                              fontSize: 12,
                              fontFamily: font,
                              cursor: 'pointer',
                              borderRadius: 6,
                              color: marketFilter === m ? C.text : C.muted,
                              background:
                                marketFilter === m ? 'rgba(255,255,255,0.08)' : 'transparent',
                              fontWeight: marketFilter === m ? 600 : 400,
                            }}
                            onMouseEnter={(e) => {
                              if (marketFilter !== m)
                                (e.currentTarget as HTMLElement).style.background =
                                  'rgba(255,255,255,0.04)';
                            }}
                            onMouseLeave={(e) => {
                              if (marketFilter !== m)
                                (e.currentTarget as HTMLElement).style.background = 'transparent';
                            }}>
                            {m}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Language filter */}
                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setLanguageDropdownOpen((o) => !o);
                        setMarketDropdownOpen(false);
                      }}
                      style={{
                        background: languageFilter ? 'var(--tx-fill-2)' : 'transparent',
                        border: 0,
                        borderRadius: 6,
                        padding: '4px 10px',
                        fontSize: 11,
                        fontWeight: 500,
                        color: languageFilter ? C.text : C.muted,
                        fontFamily: font,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                      }}>
                      {languageFilter || 'Language'}
                      <svg
                        width="8"
                        height="8"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round">
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </button>
                    {languageDropdownOpen && (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          position: 'absolute',
                          top: 28,
                          left: 0,
                          zIndex: 100,
                          background: 'var(--tx-glass)',
                          backdropFilter: 'var(--tx-blur)',
                          borderRadius: '12px',
                          padding: '6px 4px',
                          minWidth: 160,
                          boxShadow: 'var(--tx-shadow-sm)',
                        }}>
                        <div
                          onMouseDown={() => {
                            setLanguageFilter(null);
                            setLanguageDropdownOpen(false);
                          }}
                          style={{
                            padding: '6px 12px',
                            fontSize: 12,
                            fontFamily: font,
                            cursor: 'pointer',
                            borderRadius: 6,
                            color: !languageFilter ? C.text : C.muted,
                            background: !languageFilter ? 'var(--tx-fill-2)' : 'transparent',
                            fontWeight: !languageFilter ? 600 : 400,
                          }}>
                          All Languages
                        </div>
                        {allLanguages.map((l) => (
                          <div
                            key={l}
                            onMouseDown={() => {
                              setLanguageFilter(l);
                              setLanguageDropdownOpen(false);
                            }}
                            style={{
                              padding: '6px 12px',
                              fontSize: 12,
                              fontFamily: font,
                              cursor: 'pointer',
                              borderRadius: 6,
                              color: languageFilter === l ? C.text : C.muted,
                              background:
                                languageFilter === l ? 'rgba(255,255,255,0.08)' : 'transparent',
                              fontWeight: languageFilter === l ? 600 : 400,
                            }}
                            onMouseEnter={(e) => {
                              if (languageFilter !== l)
                                (e.currentTarget as HTMLElement).style.background =
                                  'rgba(255,255,255,0.04)';
                            }}
                            onMouseLeave={(e) => {
                              if (languageFilter !== l)
                                (e.currentTarget as HTMLElement).style.background = 'transparent';
                            }}>
                            {l}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                {/* Column headers */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: ADVISOR_GRID,
                    gap: 10,
                    alignItems: 'center',
                    padding: '10px 20px',
                    borderBottom: `1px solid ${C.borderLight}`,
                  }}>
                  <div />
                  {(
                    [
                      { key: 'name' as const, label: 'Name' },
                      { key: 'role' as const, label: 'Role' },
                    ] as const
                  ).map((col) => (
                    <div
                      key={col.key}
                      onClick={() => toggleSort(col.key)}
                      style={{
                        fontSize: 12,
                        fontWeight: 500,
                        color: sortCol === col.key ? C.text : C.muted,
                        fontFamily: font,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        userSelect: 'none',
                      }}>
                      {col.label}
                      <SortCaret active={sortCol === col.key} dir={sortDir} />
                    </div>
                  ))}
                  <div
                    onClick={() => toggleSort('assigned')}
                    style={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: sortCol === 'assigned' ? C.text : C.muted,
                      fontFamily: font,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      userSelect: 'none',
                    }}>
                    Assigned
                    <SortCaret active={sortCol === 'assigned'} dir={sortDir} />
                  </div>
                  {(
                    [
                      { key: 'clients' as const, label: 'Clients', center: true },
                      { key: 'deals' as const, label: 'Deals', center: true },
                      { key: 'offers' as const, label: 'Offers', center: true },
                    ] as const
                  ).map((col) => (
                    <div
                      key={col.key}
                      onClick={() => toggleSort(col.key)}
                      style={{
                        fontSize: 12,
                        fontWeight: 500,
                        color: sortCol === col.key ? C.text : C.muted,
                        fontFamily: font,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        userSelect: 'none',
                        justifyContent: col.center ? 'center' : undefined,
                      }}>
                      {col.label}
                      <SortCaret active={sortCol === col.key} dir={sortDir} />
                    </div>
                  ))}
                  <div />
                </div>

                {/* Rows */}
                {sortedAdvisors.map((advisor) => {
                  const clients = advisorClientsMap.get(advisor.userId) || [];
                  const clientCount = clients.length;
                  const displayName = advisor.name || advisor.email || advisor.username;
                  const isDragOver = dragOverAdvisorId === advisor.userId;

                  return (
                    <div
                      key={advisor.userId}
                      onClick={() => setSelectedAdvisorId(advisor.userId)}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragOverAdvisorId(advisor.userId);
                      }}
                      onDragLeave={() => setDragOverAdvisorId(null)}
                      onDrop={(e) => {
                        e.preventDefault();
                        handleDrop(advisor.userId);
                      }}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: ADVISOR_GRID,
                        gap: 10,
                        alignItems: 'center',
                        padding: '12px 20px',
                        cursor: 'pointer',
                        transition: 'background 0.15s',
                        background: isDragOver ? 'var(--tx-fill)' : 'transparent',
                        borderBottom: `1px solid ${C.borderLight}`,
                      }}
                      onMouseEnter={(e) => {
                        if (!isDragOver)
                          (e.currentTarget as HTMLElement).style.background =
                            'rgba(255,255,255,0.04)';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLElement).style.background = isDragOver
                          ? 'rgba(187,183,175,0.08)'
                          : 'transparent';
                      }}>
                      <Avatar name={displayName} size={36} photoUrl={advisor.profile?.photoUrl} />
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: C.text,
                            fontFamily: font,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                          {displayName}
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: C.subtle,
                            fontFamily: font,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                          {advisor.email || '—'}
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          fontFamily: font,
                          whiteSpace: 'nowrap',
                          width: 'fit-content',
                          color: advisor.groups?.includes('Admin') ? '#DB5282' : '#FF9A4D',
                          background: `${
                            advisor.groups?.includes('Admin') ? '#DB5282' : '#FF9A4D'
                          }18`,
                          border: 0,
                          borderRadius: 6,
                          padding: '4px 10px',
                        }}>
                        {advisor.groups?.[0]}
                      </div>
                      {/* Client avatars preview */}
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {clients.length > 0 ? (
                          <>
                            {clients.slice(0, 4).map(({ user: buyer }, i) => (
                              <div
                                key={buyer.userId}
                                style={{
                                  marginLeft: i > 0 ? -5 : 0,
                                  position: 'relative',
                                  zIndex: 4 - i,
                                }}>
                                <Avatar
                                  name={buyer.name || buyer.email || '?'}
                                  size={20}
                                  photoUrl={buyer.profile?.photoUrl}
                                />
                              </div>
                            ))}
                            {clients.length > 4 && (
                              <div
                                style={{
                                  marginLeft: -5,
                                  width: 20,
                                  height: 20,
                                  borderRadius: '50%',
                                  background: C.input,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: 10,
                                  fontWeight: 600,
                                  color: C.muted,
                                  fontFamily: font,
                                  position: 'relative',
                                }}>
                                +{clients.length - 4}
                              </div>
                            )}
                          </>
                        ) : (
                          <div
                            style={{
                              fontSize: 10,
                              color: C.subtle,
                              fontFamily: font,
                              fontStyle: 'italic',
                            }}>
                            —
                          </div>
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: clientCount > 0 ? C.text : C.subtle,
                          fontFamily: font,
                          textAlign: 'center',
                        }}>
                        {clientCount}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: advisorStatsMap.get(advisor.userId)?.dealCount ? C.text : C.subtle,
                          fontFamily: font,
                          textAlign: 'center',
                        }}>
                        {advisorStatsMap.get(advisor.userId)?.dealCount ?? 0}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: advisorStatsMap.get(advisor.userId)?.offerCount
                            ? C.text
                            : C.subtle,
                          fontFamily: font,
                          textAlign: 'center',
                        }}>
                        {advisorStatsMap.get(advisor.userId)?.offerCount ?? 0}
                      </div>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={C.subtle}
                        strokeWidth="2"
                        strokeLinecap="round">
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* ─── Card view ─────────────────────────────────────── */
              <div>
                {/* Sort bar */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '0 0 12px',
                    fontSize: 11,
                    fontFamily: font,
                    color: C.subtle,
                  }}>
                  <span style={{ fontWeight: 500 }}>Sort by</span>
                  {(
                    [
                      { key: 'name' as const, label: 'Name' },
                      { key: 'role' as const, label: 'Role' },
                      { key: 'clients' as const, label: 'Clients' },
                      { key: 'deals' as const, label: 'Deals' },
                      { key: 'offers' as const, label: 'Offers' },
                    ] as const
                  ).map((col) => (
                    <button
                      key={col.key}
                      onClick={() => toggleSort(col.key)}
                      style={{
                        background: sortCol === col.key ? 'var(--tx-fill-2)' : 'transparent',
                        border: 0,
                        borderRadius: 6,
                        padding: '4px 10px',
                        fontSize: 11,
                        fontWeight: 500,
                        color: sortCol === col.key ? C.text : C.muted,
                        fontFamily: font,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                      }}>
                      {col.label}
                      <SortCaret active={sortCol === col.key} dir={sortDir} />
                    </button>
                  ))}
                  <div style={{ flex: 1 }} />
                  <span style={{ fontWeight: 500 }}>Filter by</span>
                  {/* Market filter */}
                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setMarketDropdownOpen((o) => !o);
                        setLanguageDropdownOpen(false);
                      }}
                      style={{
                        background: marketFilter ? 'var(--tx-fill-2)' : 'transparent',
                        border: 0,
                        borderRadius: 6,
                        padding: '4px 10px',
                        fontSize: 11,
                        fontWeight: 500,
                        color: marketFilter ? C.text : C.muted,
                        fontFamily: font,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                      }}>
                      {marketFilter || 'Market'}
                      <svg
                        width="8"
                        height="8"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round">
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </button>
                    {marketDropdownOpen && (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          position: 'absolute',
                          top: 28,
                          left: 0,
                          zIndex: 100,
                          background: 'var(--tx-glass)',
                          backdropFilter: 'var(--tx-blur)',
                          borderRadius: '12px',
                          padding: '6px 4px',
                          minWidth: 160,
                          boxShadow: 'var(--tx-shadow-sm)',
                        }}>
                        <div
                          onMouseDown={() => {
                            setMarketFilter(null);
                            setMarketDropdownOpen(false);
                          }}
                          style={{
                            padding: '6px 12px',
                            fontSize: 12,
                            fontFamily: font,
                            cursor: 'pointer',
                            borderRadius: 6,
                            color: !marketFilter ? C.text : C.muted,
                            background: !marketFilter ? 'var(--tx-fill-2)' : 'transparent',
                            fontWeight: !marketFilter ? 600 : 400,
                          }}>
                          All Markets
                        </div>
                        {allMarkets.map((m) => (
                          <div
                            key={m}
                            onMouseDown={() => {
                              setMarketFilter(m);
                              setMarketDropdownOpen(false);
                            }}
                            style={{
                              padding: '6px 12px',
                              fontSize: 12,
                              fontFamily: font,
                              cursor: 'pointer',
                              borderRadius: 6,
                              color: marketFilter === m ? C.text : C.muted,
                              background:
                                marketFilter === m ? 'rgba(255,255,255,0.08)' : 'transparent',
                              fontWeight: marketFilter === m ? 600 : 400,
                            }}
                            onMouseEnter={(e) => {
                              if (marketFilter !== m)
                                (e.currentTarget as HTMLElement).style.background =
                                  'rgba(255,255,255,0.04)';
                            }}
                            onMouseLeave={(e) => {
                              if (marketFilter !== m)
                                (e.currentTarget as HTMLElement).style.background = 'transparent';
                            }}>
                            {m}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Language filter */}
                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setLanguageDropdownOpen((o) => !o);
                        setMarketDropdownOpen(false);
                      }}
                      style={{
                        background: languageFilter ? 'var(--tx-fill-2)' : 'transparent',
                        border: 0,
                        borderRadius: 6,
                        padding: '4px 10px',
                        fontSize: 11,
                        fontWeight: 500,
                        color: languageFilter ? C.text : C.muted,
                        fontFamily: font,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                      }}>
                      {languageFilter || 'Language'}
                      <svg
                        width="8"
                        height="8"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round">
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </button>
                    {languageDropdownOpen && (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          position: 'absolute',
                          top: 28,
                          left: 0,
                          zIndex: 100,
                          background: 'var(--tx-glass)',
                          backdropFilter: 'var(--tx-blur)',
                          borderRadius: '12px',
                          padding: '6px 4px',
                          minWidth: 160,
                          boxShadow: 'var(--tx-shadow-sm)',
                        }}>
                        <div
                          onMouseDown={() => {
                            setLanguageFilter(null);
                            setLanguageDropdownOpen(false);
                          }}
                          style={{
                            padding: '6px 12px',
                            fontSize: 12,
                            fontFamily: font,
                            cursor: 'pointer',
                            borderRadius: 6,
                            color: !languageFilter ? C.text : C.muted,
                            background: !languageFilter ? 'var(--tx-fill-2)' : 'transparent',
                            fontWeight: !languageFilter ? 600 : 400,
                          }}>
                          All Languages
                        </div>
                        {allLanguages.map((l) => (
                          <div
                            key={l}
                            onMouseDown={() => {
                              setLanguageFilter(l);
                              setLanguageDropdownOpen(false);
                            }}
                            style={{
                              padding: '6px 12px',
                              fontSize: 12,
                              fontFamily: font,
                              cursor: 'pointer',
                              borderRadius: 6,
                              color: languageFilter === l ? C.text : C.muted,
                              background:
                                languageFilter === l ? 'rgba(255,255,255,0.08)' : 'transparent',
                              fontWeight: languageFilter === l ? 600 : 400,
                            }}
                            onMouseEnter={(e) => {
                              if (languageFilter !== l)
                                (e.currentTarget as HTMLElement).style.background =
                                  'rgba(255,255,255,0.04)';
                            }}
                            onMouseLeave={(e) => {
                              if (languageFilter !== l)
                                (e.currentTarget as HTMLElement).style.background = 'transparent';
                            }}>
                            {l}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div
                  style={{
                    display: 'grid',
                    // vertical business cards: a set width, standing portrait, however wide the screen
                    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 280px))',
                    // every row as tall as the tallest card, so all cards match
                    gridAutoRows: '1fr',
                    gap: 20,
                  }}>
                  {sortedAdvisors.map((advisor) => {
                    const clients = advisorClientsMap.get(advisor.userId) || [];
                    const clientCount = clients.length;
                    const displayName = advisor.name || advisor.email || advisor.username;
                    const ini = initials(displayName);
                    const isDragOver = dragOverAdvisorId === advisor.userId;
                    const stats = advisorStatsMap.get(advisor.userId);
                    const markets = advisor.profile?.markets ?? [];
                    const languages = advisor.profile?.languages ?? [];
                    const isAdminAdvisor = !!advisor.groups?.includes('Admin');
                    const roleColor = isAdminAdvisor ? '#DB5282' : '#FF9A4D';

                    return (
                      <div
                        key={advisor.userId}
                        onClick={() => setSelectedAdvisorId(advisor.userId)}
                        onDragOver={(e) => {
                          e.preventDefault();
                          setDragOverAdvisorId(advisor.userId);
                        }}
                        onDragLeave={() => setDragOverAdvisorId(null)}
                        onDrop={(e) => {
                          e.preventDefault();
                          handleDrop(advisor.userId);
                        }}
                        style={{
                          // a business card: raised glass with the theme's top light and a soft drop, lifting a little
                          // on hover; a client dragged over it shows where it will land
                          background: isDragOver ? 'var(--tx-sel-bg)' : 'var(--tx-glass-panel)',
                          backgroundImage: isDragOver ? undefined : 'var(--tx-surface-light)',
                          boxShadow: isDragOver
                            ? 'inset 0 0 0 1.5px var(--tx-muted)'
                            : 'var(--tx-shadow-sm), inset 0 1px 0 rgba(255,255,255,0.06)',
                          borderRadius: 24,
                          padding: 24,
                          aspectRatio: '5 / 7',
                          cursor: 'pointer',
                          transition: 'transform 0.2s ease, box-shadow 0.2s ease, background 0.15s',
                          display: 'flex',
                          flexDirection: 'column',
                          // clusters (face, name, contact, book, chips) stand apart; each stays tight inside
                          gap: 22,
                          fontFamily: font,
                        }}
                        onMouseEnter={(e) => {
                          if (isDragOver) return;
                          const el = e.currentTarget as HTMLElement;
                          el.style.transform = 'translateY(-2px)';
                          el.style.boxShadow = 'var(--tx-shadow), inset 0 1px 0 rgba(255,255,255,0.08)';
                        }}
                        onMouseLeave={(e) => {
                          const el = e.currentTarget as HTMLElement;
                          el.style.transform = 'none';
                          el.style.boxShadow = 'var(--tx-shadow-sm), inset 0 1px 0 rgba(255,255,255,0.06)';
                        }}>
                        {/* face: the round portrait, and the card's one move (matching an inbound client) opposite */}
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                          <div
                            style={{
                              width: 56,
                              height: 56,
                              borderRadius: '50%',
                              overflow: 'hidden',
                              flexShrink: 0,
                              background: 'var(--tx-fill-2)',
                              boxShadow: '0 6px 16px -6px rgba(0,0,0,0.5)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}>
                            {advisor.profile?.photoUrl ? (
                              <img
                                src={advisor.profile.photoUrl}
                                alt={displayName}
                                style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: '50% 30%' }}
                              />
                            ) : (
                              <span style={{ fontSize: 20, fontWeight: 300, letterSpacing: '-0.03em', color: C.muted }}>
                                {ini}
                              </span>
                            )}
                          </div>
                          <AssignPicker
                            advisorMarkets={markets}
                            clients={unassignedBuyers}
                            onAssign={(buyerId) => handleAssign(advisor.userId, buyerId)}
                          />
                        </div>

                        {/* name, and the role as the app's tick; it and the contact rows settle down onto the foot */}
                        <div style={{ minWidth: 0, marginTop: 'auto' }}>
                          <div
                            style={{
                              fontSize: 17,
                              fontWeight: 400,
                              letterSpacing: '-0.015em',
                              color: 'var(--tx-text)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                            {displayName}
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                              marginTop: 4,
                              fontSize: 12,
                              fontWeight: 500,
                              color: C.muted,
                            }}>
                            <span style={{ width: 2, height: 12, borderRadius: 1, background: roleColor }} />
                            {isAdminAdvisor ? 'Admin' : 'Real estate advisor'}
                          </div>
                        </div>

                        {/* how to reach them */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          {(
                            [
                              ['mail', advisor.email || '—'],
                              ['phone', formatPhone(advisor.profile?.phoneNumber)],
                              ['award', advisor.profile?.dreNumber ? `DRE #${advisor.profile.dreNumber}` : '—'],
                            ] as const
                          ).map(([icon, text]) => (
                            <div
                              key={icon}
                              style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, color: C.subtle }}>
                              <Icon name={icon} size={14} />
                              <span
                                style={{
                                  fontSize: 12,
                                  fontWeight: 500,
                                  color: C.muted,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}>
                                {text}
                              </span>
                            </div>
                          ))}
                        </div>

                        {/* the card's foot, under a hairline: their book, then where they work and what they speak */}
                        <div
                          style={{
                            paddingTop: 18,
                            borderTop: '1px solid var(--tx-line)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 14,
                          }}>
                        <ReachMenu markets={markets} languages={languages} />
                        {/* their book in even thirds, parted by subtle hairlines */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', fontSize: 13 }}>
                          {(
                            [
                              [clientCount, clientCount === 1 ? 'Client' : 'Clients'],
                              [stats?.dealCount ?? 0, (stats?.dealCount ?? 0) === 1 ? 'Deal' : 'Deals'],
                              [stats?.offerCount ?? 0, (stats?.offerCount ?? 0) === 1 ? 'Offer' : 'Offers'],
                            ] as const
                          ).map(([n, label], i) => (
                            <span
                              key={label}
                              style={{
                                whiteSpace: 'nowrap',
                                paddingLeft: i ? 12 : 0,
                                borderLeft: i ? '1px solid var(--tx-line)' : 'none',
                              }}>
                              <b style={{ fontWeight: 600, color: n ? C.text : C.subtle, fontVariantNumeric: 'tabular-nums' }}>
                                {n}
                              </b>{' '}
                              <span style={{ fontWeight: 500, color: C.muted }}>{label}</span>
                            </span>
                          ))}
                        </div>

                        {/* who they're working with: the row is always there, so every card is the same height */}
                        {clients.length === 0 ? (
                          <div style={{ height: 24, display: 'flex', alignItems: 'center', fontSize: 12, fontWeight: 500, color: C.subtle }}>
                            No clients yet
                          </div>
                        ) : (
                          <div style={{ height: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              {clients.slice(0, 5).map(({ user: buyer }, i) => (
                                <div
                                  key={buyer.userId}
                                  title={buyer.name || buyer.email}
                                  style={{
                                    marginLeft: i > 0 ? -6 : 0,
                                    position: 'relative',
                                    zIndex: 5 - i,
                                    borderRadius: '50%',
                                    boxShadow: '0 0 0 2px var(--tx-glass-panel)',
                                  }}>
                                  <Avatar name={buyer.name || buyer.email || '?'} size={24} photoUrl={buyer.profile?.photoUrl} />
                                </div>
                              ))}
                            </div>
                            {clients.length > 5 && (
                              <span style={{ fontSize: 12, fontWeight: 500, color: C.muted }}>+{clients.length - 5}</span>
                            )}
                          </div>
                        )}

                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ─── Right panel: Clients ─────────────────────────── */}
        <div
          style={{
            background: C.card,
            backgroundImage: 'var(--tx-surface-light)',
            boxShadow: 'var(--tx-shadow)',
            borderRadius: '24px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}>
          {/* Header */}
          <div
            style={{
              padding: '16px 20px',
              borderBottom: `1px solid ${C.borderLight}`,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 300,
                color: C.muted,
                fontFamily: font,
              }}>
              Clients
            </div>
            <div style={{ flex: 1 }} />
            {unassignedCount > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 500, color: C.muted, fontFamily: font }}>
                <span style={{ width: 2, height: 12, borderRadius: 1, background: '#DB5282' }} />
                Inbound
                <span style={{ color: C.text, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{unassignedCount}</span>
              </div>
            )}
          </div>

          {/* Search */}
          <div style={{ padding: '12px 16px 8px' }}>
            <div style={{ position: 'relative' }}>
              <svg
                style={{
                  position: 'absolute',
                  left: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                }}
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke={C.subtle}
                strokeWidth="2"
                strokeLinecap="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                placeholder="Search clients…"
                style={{
                  width: '100%',
                  height: 32,
                  background: 'var(--tx-fill)',
                  border: 0,
                  borderRadius: 8,
                  padding: '0 12px 0 32px',
                  color: C.text,
                  fontSize: 12,
                  fontFamily: font,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = C.muted;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = C.border;
                }}
              />
            </div>
          </div>

          {/* Client list */}
          <div style={{ flex: 1, overflow: 'auto', padding: '4px 12px 12px' }}>
            {buyers.length === 0 && !isLoading ? (
              <div
                style={{
                  padding: 40,
                  textAlign: 'center',
                  fontSize: 13,
                  color: C.subtle,
                  fontFamily: font,
                }}>
                No clients found
              </div>
            ) : (
              filteredClients.map((buyer) => {
                const isAssigned = assignedBuyerIds.has(buyer.userId);
                const assignedTo = isAssigned ? buyerRelationshipMap.get(buyer.userId) : null;
                const advisorUser = assignedTo ? userMap.get(assignedTo.advisorId) : null;
                const displayName = buyer.name || buyer.email || buyer.username;
                const isDragging = dragBuyerId === buyer.userId;

                return (
                  <div
                    key={buyer.userId}
                    draggable={!isAssigned}
                    onDragStart={() => handleDragStart(buyer.userId)}
                    onDragEnd={handleDragEnd}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 12px',
                      borderRadius: 10,
                      marginBottom: 3,
                      transition: 'all 0.15s',
                      background: isDragging ? 'var(--tx-fill)' : 'transparent',
                      opacity: isDragging ? 0.5 : 1,
                      cursor: isAssigned ? 'default' : 'grab',
                      border: !isAssigned
                        ? `1px dashed rgba(255,255,255,0.08)`
                        : '1px solid transparent',
                    }}
                    onMouseEnter={(e) => {
                      if (!isDragging)
                        (e.currentTarget as HTMLElement).style.background =
                          'rgba(255,255,255,0.04)';
                    }}
                    onMouseLeave={(e) => {
                      if (!isDragging)
                        (e.currentTarget as HTMLElement).style.background = isDragging
                          ? 'rgba(187,183,175,0.06)'
                          : 'transparent';
                    }}>
                    <Avatar name={displayName} size={28} photoUrl={buyer.profile?.photoUrl} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: C.text,
                          fontFamily: font,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                        {displayName}
                      </div>
                      {buyer.name && buyer.email && (
                        <div
                          style={{
                            fontSize: 11,
                            color: C.subtle,
                            fontFamily: font,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                          {buyer.email}
                        </div>
                      )}
                    </div>
                    {isAssigned ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke={C.lime}
                          strokeWidth="2.5"
                          strokeLinecap="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        <span
                          style={{
                            fontSize: 10,
                            color: C.subtle,
                            fontFamily: font,
                            maxWidth: 80,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                          {advisorUser?.name || advisorUser?.email || 'Assigned'}
                        </span>
                      </div>
                    ) : (
                      // an inbound client still waiting on a match: the app's tick, in the critical pink
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                        <span style={{ width: 2, height: 12, borderRadius: 1, background: '#DB5282' }} />
                        <span style={{ fontSize: 11, color: C.muted, fontFamily: font, fontWeight: 500 }}>
                          Inbound
                        </span>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* ─── Advisor detail drawer ────────────────────────────── */}
      {selectedAdvisor && (
        <AdvisorDrawer
          advisor={selectedAdvisor}
          clients={selectedAdvisorClients}
          allBuyers={buyers}
          allAssignedBuyerIds={assignedBuyerIds}
          isPending={createMutation.isPending || deleteMutation.isPending}
          onClose={() => setSelectedAdvisorId(null)}
          onAssign={handleAssign}
          onUnassign={(relId) => {
            const rel = relationships.find((r) => r.id === relId);
            const buyer = rel ? userMap.get(rel.buyerId) : null;
            const advisor = rel ? userMap.get(rel.advisorId) : null;
            setConfirmUnassign({
              relationshipId: relId,
              buyerName: buyer?.name || buyer?.email || 'this client',
              advisorName: advisor?.name || advisor?.email || 'this advisor',
            });
          }}
        />
      )}

      {/* ─── Unassign confirmation ────────────────────────────── */}
      {confirmUnassign &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--tx-backdrop)',
              backdropFilter: 'blur(4px)',
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setConfirmUnassign(null);
            }}>
            <div
              style={{
                background: C.card,
                backgroundImage: 'var(--tx-surface-light)',
                boxShadow: 'var(--tx-shadow)',
                borderRadius: '24px',
                padding: 24,
                width: 400,
                maxWidth: '90vw',
              }}>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: C.text,
                  fontFamily: font,
                  marginBottom: 8,
                }}>
                Remove assignment
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: C.muted,
                  fontFamily: font,
                  marginBottom: 20,
                  lineHeight: 1.5,
                }}>
                Remove <strong style={{ color: C.text }}>{confirmUnassign.buyerName}</strong> from{' '}
                <strong style={{ color: C.text }}>{confirmUnassign.advisorName}</strong>?
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setConfirmUnassign(null)}
                  style={{
                    background: 'transparent',
                    color: C.muted,
                    border: 'none',
                    borderRadius: 8,
                    padding: '8px 16px',
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: font,
                    cursor: 'pointer',
                  }}>
                  Cancel
                </button>
                <button
                  onClick={() => handleUnassign(confirmUnassign.relationshipId)}
                  disabled={deleteMutation.isPending}
                  style={{
                    background: deleteMutation.isPending ? C.subtle : C.danger,
                    color: 'var(--tx-text)',
                    border: 'none',
                    borderRadius: 8,
                    padding: '8px 16px',
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: font,
                    cursor: deleteMutation.isPending ? 'not-allowed' : 'pointer',
                    opacity: deleteMutation.isPending ? 0.6 : 1,
                  }}>
                  {deleteMutation.isPending ? 'Removing…' : 'Remove'}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};
